/**
 * Privacy Utilities for Sentlo
 * Protects user privacy by anonymizing names in posts
 * Created: 2026-03-11
 */

// Log successful import
console.log('✅ Privacy Utils Loaded - 2026-03-11');

/**
 * Generate anonymous username from user ID
 * Returns consistent username for same user
 */
export function generateAnonymousUsername(userId: string, userName?: string): string {
  // If it's an official account, keep the name
  if (userName?.toLowerCase().includes('official') || 
      userName?.toLowerCase().includes('lastma') || 
      userName?.toLowerCase().includes('frsc') ||
      userName?.toLowerCase().includes('verified')) {
    return userName;
  }

  // Generate consistent hash from userId
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    const char = userId.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  // Make it positive
  hash = Math.abs(hash);
  
  // Generate username format: "Reporter_12345"
  const userNumber = (hash % 99999) + 1; // Number between 1 and 99999
  return `Reporter_${userNumber.toString().padStart(5, '0')}`;
}

/**
 * Generate anonymous initials from username
 * Returns 2 letters for avatar
 */
export function generateAnonymousInitials(userId: string): string {
  // Generate consistent hash from userId
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    const char = userId.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  
  hash = Math.abs(hash);
  
  // Generate two letters from hash
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const firstLetter = letters[hash % 26];
  const secondLetter = letters[(hash >> 5) % 26];
  
  return `${firstLetter}${secondLetter}`;
}

/**
 * Check if a username should be displayed publicly (official accounts)
 */
export function shouldShowRealName(userName: string): boolean {
  const officialKeywords = [
    'official', 'lastma', 'frsc', 'verified', 'government', 
    'agency', 'ministry', 'commission', 'authority'
  ];
  
  const lowerName = userName.toLowerCase();
  return officialKeywords.some(keyword => lowerName.includes(keyword));
}

/**
 * Get display name for a user (anonymous or real based on privacy settings)
 */
export function getDisplayName(userId: string, userName: string, isOwnPost: boolean = false): string {
  // Show "You" for user's own posts
  if (isOwnPost) {
    return 'You';
  }
  
  // Show real name for official accounts
  if (shouldShowRealName(userName)) {
    return userName;
  }
  
  // Show anonymous name for all other users
  return generateAnonymousUsername(userId, userName);
}
